/**
 * 商談詳細「個別説明」タブ。
 *  - 確定済み schedule の表示 (deal.scheduledBriefingAt から)
 *  - 未確定なら ConfirmBriefingDateDialog 起動
 *  - briefing_records の表示 + 「記録ページへ」リンク
 *  - 各記録の「検討」グループを個別行リスト化し、行ごとに「見積書作成」ボタン
 */
import type { ConsideredProductItem } from '@goenhub/shared';
import {
  BRIEFING_DELETED_SLOT_LABEL,
  BRIEFING_STYLE_LABEL,
  dealBriefingCandidates,
  dealBriefingSessions,
  lineDiscountLabel,
  resolveBriefingStyle,
} from '@goenhub/shared';
import {
  Calendar,
  CalendarCheck,
  Edit,
  ExternalLink,
  MessageSquare,
  Plus,
  Receipt,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { BriefingCandidateDecisionDialog } from '../../briefings/BriefingCandidateDecisionDialog';
import { ConfirmBriefingDateDialog } from '../../briefings/ConfirmBriefingDateDialog';
import { DocumentMailDialog } from '../components/DocumentMailDialog';
import { AddBriefingRecordDialog } from '../dialogs/AddBriefingRecordDialog';
import { CreateQuoteDialog } from '../dialogs/CreateQuoteDialog';

import { EmptyState } from '@/components/common/EmptyState';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useAdminDirectory } from '@/hooks/useAdmins';
import {
  useBriefingRecords,
  useBriefingSchedules,
  useDeletedBriefingScheduleIds,
  useSubmissionBriefingCandidateFallback,
} from '@/hooks/useBriefings';
import { type DealRecord } from '@/hooks/useDeals';
import { paths } from '@/routes/paths';

interface Props {
  deal: DealRecord;
  /** 見積書送信先メール (DealDetailPage が contact から供給) */
  recipientEmail?: string | null;
  /** 顧客名 (見積書件名のテンプレートに使用) */
  contactName?: string | null;
}

interface QuoteDialogState {
  open: boolean;
  initialItems: ConsideredProductItem[];
  title: string;
}

export function DealBriefingTab({ deal, recipientEmail, contactName }: Props) {
  const records = useBriefingRecords(deal.id);
  const schedules = useBriefingSchedules();
  // 日程確定ダイアログ。replaceScheduleId が入っていれば「その回の日程変更」、
  // null なら「個別説明を追加 (別セッション)」として確定する。
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    replaceScheduleId: string | null;
  }>({ open: false, replaceScheduleId: null });
  const [addRecordOpen, setAddRecordOpen] = useState(false);
  const [quoteDialog, setQuoteDialog] = useState<QuoteDialogState>({
    open: false,
    initialItems: [],
    title: '',
  });
  // 「保存して送信」で作成した下書きのメール送信モーダル対象 (DealQuotesTab と同じ導線)
  const [mailQuoteId, setMailQuoteId] = useState<string | null>(null);

  const openQuoteForGroup = (items: ConsideredProductItem[], groupLabel: string) => {
    const namePart = contactName ? `${contactName} 様 ` : '';
    setQuoteDialog({
      open: true,
      initialItems: items,
      title: `${namePart}お見積書（${groupLabel}）`,
    });
  };

  const scheduledAt = useMemo(() => tsToDate(deal.scheduledBriefingAt), [deal]);
  // 確定済みセッション一覧 (旧データは単一フィールドから 1 件に昇格)
  const sessions = useMemo(() => dealBriefingSessions(deal), [deal]);
  // フォーム送信時に顧客が選択した候補枠 (resolveDealForSubmission が Deal に保存)
  const allCandidates = useMemo(() => dealBriefingCandidates(deal), [deal]);
  // 旧申込 (候補未保存) は form_submissions の回答から候補日を復元する
  const scheduleIdList = useMemo(() => (schedules.data ?? []).map((s) => s.id), [schedules.data]);
  const needsFallback = allCandidates.length === 0 && !scheduledAt;
  const { data: fallbackMap } = useSubmissionBriefingCandidateFallback(
    needsFallback ? [deal] : [],
    scheduleIdList,
  );
  const pendingCandidates =
    allCandidates.length > 0
      ? allCandidates.filter((c) => c.status === 'pending')
      : (fallbackMap?.get(deal.id) ?? []);
  const archivedCandidates = allCandidates.filter(
    (c) => c.status === 'declined' || c.status === 'cancelled',
  );
  const candidateIds = (allCandidates.length > 0 ? allCandidates : pendingCandidates).map(
    (c) => c.scheduleId,
  );
  const [decisionDialog, setDecisionDialog] = useState<{
    open: boolean;
    candidates: typeof pendingCandidates;
  }>({ open: false, candidates: [] });
  const scheduleOf = (id: string) => schedules.data?.find((s) => s.id === id) ?? null;
  // 枠が見つからない候補: 削除済みなら「削除された候補日」、判定できなければ id のまま
  const deletedScheduleIds = useDeletedBriefingScheduleIds(candidateIds);
  const missingSlotLabel = (id: string) =>
    deletedScheduleIds.has(id) ? BRIEFING_DELETED_SLOT_LABEL : id;
  // 担当者名の解決 (owner 以外でも表示できるようディレクトリ Callable 経由)
  const { data: directory } = useAdminDirectory();
  const assigneeLabel = (uid: string): string => {
    const a = directory?.find((x) => x.uid === uid);
    return a ? a.fullName || a.displayName || a.email || uid : uid;
  };
  const isLoading = records.isLoading || schedules.isLoading;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* 左: スケジュール */}
      <div className="space-y-4">
        <Section
          title="個別説明スケジュール"
          subtitle={scheduledAt ? '日程確定済み' : 'まだ確定していません'}
        >
          {scheduledAt ? (
            <div className="space-y-3">
              {/* 確定済みセッション一覧 (1商談で複数回の個別説明に対応、2026-08-18) */}
              {sessions.map((sess, idx) => (
                <div
                  key={`${sess.scheduleId}-${idx}`}
                  className="rounded border border-[var(--border)] bg-[var(--bg-elevated)] px-3 py-2 space-y-1"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <CalendarCheck className="size-4 text-[var(--accent-green)]" />
                    <span className="text-sm font-medium text-[var(--text-primary)]">
                      {fmtDateTime(tsToDate(sess.scheduledAt))}
                    </span>
                    {sessions.length > 1 && <Badge variant="default">{idx + 1}回目</Badge>}
                    <span className="text-xs text-[var(--text-muted)]">
                      参加{Math.max(1, sess.participantCount ?? 1)}名
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-[var(--text-muted)]">
                      {sess.assigneeUid ? `担当者: ${assigneeLabel(sess.assigneeUid)}` : ''}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setConfirmDialog({
                          open: true,
                          replaceScheduleId: sess.scheduleId,
                        })
                      }
                      disabled={deal.status === 'closed'}
                      title="この回の日程を別の枠に変更する"
                    >
                      <Edit className="size-3.5" />
                      日程を変更
                    </Button>
                  </div>
                </div>
              ))}
              {/* 未処理の候補 (再申込) があれば、追加セッションとして確定できる */}
              {pendingCandidates.length > 0 && (
                <div className="space-y-1.5 pt-1">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--accent-amber)]">
                    新しい申込の候補日 ({pendingCandidates.length})
                  </div>
                  <p className="text-[11px] text-[var(--text-muted)]">
                    確定すると、既存の日程は残したまま「次回の個別説明」として追加されます。
                  </p>
                  {pendingCandidates.map((c) => {
                    const s = scheduleOf(c.scheduleId);
                    return (
                      <div
                        key={c.scheduleId}
                        className="flex items-center gap-2 rounded border border-[var(--border)] px-2.5 py-2 text-xs"
                      >
                        <Calendar className="size-3.5 text-[var(--text-muted)] flex-shrink-0" />
                        <span className="min-w-0 flex-1 tabular-nums">
                          {s
                            ? `${s.date} ${s.startTime}〜${s.endTime}`
                            : missingSlotLabel(c.scheduleId)}
                        </span>
                        <Button
                          variant="primary"
                          size="sm"
                          disabled={deal.status === 'closed'}
                          onClick={() => setDecisionDialog({ open: true, candidates: [c] })}
                        >
                          確定
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setConfirmDialog({ open: true, replaceScheduleId: null })}
                disabled={deal.status === 'closed'}
                title="別の枠を選んで個別説明を追加する"
              >
                <Plus className="size-3.5" />
                個別説明を追加
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-[var(--text-muted)]">
                {pendingCandidates.length > 0
                  ? '顧客が選択した候補日です。枠を選んで確定してください（確定すると他の候補は自動キャンセルされます）。'
                  : '顧客が個別説明を申し込んでいる場合、候補日から枠を選んで確定してください。'}
              </p>
              {pendingCandidates.length > 0 && (
                <div className="space-y-1.5">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
                    申込時の候補日 ({pendingCandidates.length})
                  </div>
                  {pendingCandidates.map((c) => {
                    const s = scheduleOf(c.scheduleId);
                    return (
                      <div
                        key={c.scheduleId}
                        className="flex items-center gap-2 rounded border border-[var(--border)] bg-[var(--bg-elevated)] px-2.5 py-2 text-xs"
                      >
                        <Calendar className="size-3.5 text-[var(--text-muted)] flex-shrink-0" />
                        {s ? (
                          <div className="min-w-0 flex-1">
                            <span className="tabular-nums font-medium">
                              {s.date} {s.startTime}〜{s.endTime}
                            </span>
                            <span className="text-[var(--text-muted)] ml-2">
                              {/* 「両方可」枠は顧客が選んだ形式を表示 */}
                              {(() => {
                                const st = resolveBriefingStyle(s.style, c.selectedStyle);
                                return `${BRIEFING_STYLE_LABEL[st]}${
                                  st !== 'online' && s.location ? ` @ ${s.location}` : ''
                                }`;
                              })()}
                              {' ・ 参加'}
                              {Math.max(1, c.participantCount ?? 1)}名{' ・ 受入'}
                              {s.capacity}名
                              {(s.bookedCount ?? 0) > 0 && (
                                <span className="text-[var(--accent-red)]">{' ・ 予約済み'}</span>
                              )}
                            </span>
                          </div>
                        ) : (
                          <span className="min-w-0 flex-1 text-[var(--text-muted)]">
                            {deletedScheduleIds.has(c.scheduleId) ? (
                              BRIEFING_DELETED_SLOT_LABEL
                            ) : (
                              <span className="font-mono">
                                {c.scheduleId}（枠が見つかりません）
                              </span>
                            )}
                          </span>
                        )}
                        <Button
                          variant="primary"
                          size="sm"
                          disabled={deal.status === 'closed'}
                          onClick={() => setDecisionDialog({ open: true, candidates: [c] })}
                        >
                          確定
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
              <Button
                variant={pendingCandidates.length > 0 ? 'secondary' : 'primary'}
                size="sm"
                onClick={() => setConfirmDialog({ open: true, replaceScheduleId: null })}
                disabled={deal.status === 'closed'}
              >
                <Plus className="size-3.5" />
                {pendingCandidates.length > 0 ? '別の枠から確定する' : '日程を確定する'}
              </Button>
            </div>
          )}
          {archivedCandidates.length > 0 && (
            <div className="mt-3 space-y-1 opacity-60">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
                処理済みの候補日
              </div>
              {archivedCandidates.map((c) => {
                const s = scheduleOf(c.scheduleId);
                return (
                  <div
                    key={`${c.scheduleId}-${c.formSubmissionId ?? ''}`}
                    className="flex items-center gap-2 rounded border border-[var(--border)] px-2.5 py-1.5 text-[11px] text-[var(--text-muted)]"
                  >
                    <span className="tabular-nums">
                      {s
                        ? `${s.date} ${s.startTime}〜${s.endTime}`
                        : missingSlotLabel(c.scheduleId)}
                    </span>
                    <Badge variant="default">
                      {c.status === 'declined' ? '拒否' : 'キャンセル'}
                    </Badge>
                  </div>
                );
              })}
            </div>
          )}
        </Section>
      </div>

      {/* 右: 記録 */}
      <div className="space-y-4">
        <Section
          title="個別説明記録"
          subtitle={
            records.data && records.data.length > 0 ? `${records.data.length} 件` : '未記録'
          }
          action={
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setAddRecordOpen(true)}
                disabled={deal.status === 'closed'}
                title="スケジュールなしで記録を直接追加"
              >
                <Plus className="size-3.5" />
                記録を追加
              </Button>
              <Link
                to={paths.briefingRecord.replace(':dealId', deal.id)}
                className="inline-flex items-center gap-1 text-xs text-[var(--accent)] hover:underline"
              >
                <ExternalLink className="size-3" />
                記録ページを開く
              </Link>
            </div>
          }
        >
          {isLoading ? (
            <Spinner />
          ) : !records.data || records.data.length === 0 ? (
            <EmptyState
              icon={MessageSquare}
              title="記録がまだありません"
              description="個別説明を実施したら「記録ページを開く」から内容を残してください。"
            />
          ) : (
            <div className="space-y-2">
              {records.data.map((r) => (
                <div
                  key={r.id}
                  className="rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] p-3 space-y-1.5"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Calendar className="size-3.5 text-[var(--text-muted)]" />
                      <span className="text-xs font-medium">
                        {fmtDateTime(tsToDate(r.conductedAt))}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {(r.status ?? 'completed') === 'draft' && (
                        <Badge variant="draft">下書き</Badge>
                      )}
                      <Badge variant="accent">成約レベル {r.conversionLevel}</Badge>
                    </div>
                  </div>
                  {r.consideredProducts && r.consideredProducts.length > 0 && (
                    <div className="space-y-1.5 pt-1">
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
                        検討
                      </div>
                      {r.consideredProducts.map((g, idx) => {
                        const label = g.label?.trim() || `検討${String(idx + 1).padStart(2, '0')}`;
                        return (
                          <div
                            key={idx}
                            className="flex items-start justify-between gap-2 rounded border border-[var(--border)] bg-[var(--bg-surface)] px-2.5 py-1.5"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="text-[11px] font-medium text-[var(--text-secondary)]">
                                {label}
                              </div>
                              <div className="text-xs text-[var(--text-muted)] mt-0.5 break-words">
                                {g.items.length > 0
                                  ? g.items
                                      .map((it) => {
                                        const discount = lineDiscountLabel(
                                          it.discountType,
                                          it.discountValue,
                                        );
                                        return `${it.productName}×${it.quantity}${
                                          discount ? `（値引き ${discount}）` : ''
                                        }`;
                                      })
                                      .join(', ')
                                  : '（製品未登録）'}
                              </div>
                            </div>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => openQuoteForGroup(g.items, label)}
                              disabled={deal.status === 'closed' || g.items.length === 0}
                              title={
                                g.items.length === 0
                                  ? '製品が未登録のため見積書を作成できません'
                                  : '見積書作成ダイアログを開く'
                              }
                            >
                              <Receipt className="size-3.5" />
                              見積書作成
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {r.inquiries && (
                    <div className="text-xs text-[var(--text-muted)] line-clamp-2">
                      {r.inquiries}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      <ConfirmBriefingDateDialog
        open={confirmDialog.open}
        dealId={deal.id}
        candidateScheduleIds={candidateIds}
        salesAgentUid={deal.salesAgentUid ?? null}
        mode={confirmDialog.replaceScheduleId ? 'reschedule' : 'additional'}
        replaceScheduleId={confirmDialog.replaceScheduleId}
        onOpenChange={(o) => setConfirmDialog((p) => ({ ...p, open: o }))}
      />

      <BriefingCandidateDecisionDialog
        open={decisionDialog.open}
        dealId={deal.id}
        candidates={decisionDialog.candidates}
        onOpenChange={(o) => setDecisionDialog((prev) => ({ ...prev, open: o }))}
      />

      <AddBriefingRecordDialog
        open={addRecordOpen}
        onOpenChange={setAddRecordOpen}
        dealId={deal.id}
      />

      <CreateQuoteDialog
        open={quoteDialog.open}
        onOpenChange={(open) => setQuoteDialog((prev) => ({ ...prev, open }))}
        dealId={deal.id}
        contactId={deal.contactId}
        defaultRecipientEmail={recipientEmail ?? null}
        defaultTitle={quoteDialog.title}
        initialItems={quoteDialog.initialItems}
        onCreatedForSend={(quoteId) => setMailQuoteId(quoteId)}
      />

      <DocumentMailDialog
        open={mailQuoteId !== null}
        onOpenChange={(o) => !o && setMailQuoteId(null)}
        documentType="quote"
        dealId={deal.id}
        documentId={mailQuoteId}
      />
    </div>
  );
}

// ─────────────────────────────────────────────
// 共通
// ─────────────────────────────────────────────

function Section({
  title,
  subtitle,
  action,
  children,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-4">
      <div className="flex items-start justify-between mb-3 gap-2">
        <div>
          <h3 className="text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider">
            {title}
          </h3>
          {subtitle && <p className="text-[11px] text-[var(--text-muted)] mt-0.5">{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function tsToDate(v: unknown): Date | null {
  if (!v) return null;
  if (typeof v === 'string') {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    try {
      return (v as { toDate: () => Date }).toDate();
    } catch {
      return null;
    }
  }
  return null;
}

function fmtDateTime(d: Date | null): string {
  if (!d) return '—';
  return d.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}
